import {
  Avatar,
  Button,
  Form,
  Input,
  List,
  Radio,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  related_comments_ticket_path,
  ticket_comments_path,
} from 'js-routes/generated/routes';
import useErrors from 'hooks/forms/useErrors';
import I18n from 'i18n-js';
import classNames from 'classnames';
import {
  string, shape, number, bool, func,
} from 'prop-types';
import CommentList from './CommentList';
import CommentEditor from './CommentEditor';
import CommentAttachments from './CommentAttachments';
import CommentAttachmentButton from './CommentAttachmentButton';
import { CommentQuote } from './CommentQuote';
import { commentExcerpt } from './helpers/commentExcerpt';
import { scrollToComment } from './helpers/scrollToComment';
import useSubmitForm from './hooks/useSubmitForm';
import useUpdateComment from './hooks/useUpdateComment';

const { Text } = Typography;

const TicketComments = ({
  ticketId = null,
  currentUserInfo = {
    id: null,
    type: '',
    username: '',
    avatarUrl: '',
  },
  canSetInternalComment = false,
  canComment = true,
  onDraftChange = (_hasDraft) => {},
}) => {
  const [form] = Form.useForm();
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editedComments, setEditedComments] = useState({});

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true);

    const loadComments = async () => {
      const response = await fetch(related_comments_ticket_path(ticketId));
      const data = await response.json();
      setComments(data.comments);
      setIsLoading(false);
    };

    void (async () => {
      try {
        await loadComments();
      } catch (error) {
        // Background failures must be reported without rejecting the event handler.
        console.error(error); // eslint-disable-line no-console
        setIsLoading(false);
      }
    })();
  }, [ticketId]);

  const {
    handleError,
    clearErrors,
  } = useErrors();

  const { handleUpdateComment } = useUpdateComment({ setComments });

  const {
    isFormSending,
    handleSubmitForm,
    submitButtonName,
  } = useSubmitForm({
    form,
    formUrl: ticket_comments_path(),
    clearErrors,
    handleError,
    setComments,
    attachments,
    setAttachments,
  });

  const isInternal = Form.useWatch('internal', form);
  const content = Form.useWatch('content', form);
  const parentId = Form.useWatch('parent_id', form);

  const replyTarget = comments.find((item) => item.id === parentId) ?? null;
  const visibilityBeforeForcedInternal = useRef(null);

  const restoreForcedVisibility = useCallback(() => {
    if (visibilityBeforeForcedInternal.current === null) return;

    form.setFieldValue('internal', visibilityBeforeForcedInternal.current);
    visibilityBeforeForcedInternal.current = null;
  }, [form]);

  const handleReply = useCallback((commentId, commentIsInternal) => {
    if (form.getFieldValue('parent_id') == null) {
      visibilityBeforeForcedInternal.current = null;
    }

    if (commentIsInternal) {
      visibilityBeforeForcedInternal.current ??= form.getFieldValue('internal');
      form.setFieldValue('internal', true);
    } else {
      restoreForcedVisibility();
    }

    form.setFieldValue('parent_id', commentId);
  }, [form, restoreForcedVisibility]);

  const handleCancelReply = useCallback(() => {
    restoreForcedVisibility();
    form.setFieldValue('parent_id', null);
  }, [form, restoreForcedVisibility]);

  const handleEditDraftChange = useCallback((commentId, hasDraft) => {
    setEditedComments((prev) => (
      prev[commentId] === hasDraft ? prev : { ...prev, [commentId]: hasDraft }
    ));
  }, []);

  const hasEditedComment = Object.values(editedComments).some(Boolean);

  useEffect(() => {
    const hasNewComment = Boolean(content?.trim()) || attachments.length > 0;

    onDraftChange(hasNewComment || hasEditedComment);

    return () => onDraftChange(false);
  }, [content, attachments, hasEditedComment, onDraftChange]);

  const editorClassName = classNames('ticket-comments-form__editor', {
    'ticket-comments-form__editor--internal': isInternal,
  });

  return (
    <>
      {(isLoading || comments.length > 0) && (
        <CommentList
          ticketId={ticketId}
          comments={comments}
          loading={isLoading}
          onUpdate={handleUpdateComment}
          canSetInternalComment={canSetInternalComment}
          canReply={canComment}
          onReply={handleReply}
          onCommentDraftChange={handleEditDraftChange}
        />
      )}

      {canComment ? (
        <List.Item>
          <div className="ticket-comments-form">
            <Avatar
              src={currentUserInfo.avatarUrl}
              size={40}
            />

            <div className={editorClassName}>
              <Form
                component={false}
                form={form}
                layout="vertical"
                onFinish={handleSubmitForm}
                /* eslint-disable camelcase */
                initialValues={{
                  author_type: currentUserInfo.type,
                  author_id: currentUserInfo.id,
                  ticket_id: ticketId,
                  internal: false,
                  parent_id: null,
                }}
                /* eslint-enable camelcase */
                disabled={isFormSending}
              >
                <Form.Item name="ticket_id" hidden>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item name="author_type" hidden>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item name="author_id" hidden>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item name="parent_id" hidden>
                  <Input type="hidden" />
                </Form.Item>

                {replyTarget && (
                  <div className="ticket-comments-form__reply-to">
                    <Text type="secondary" className="ticket-comments-form__reply-to-label">
                      {I18n.t('ticket_comments.replying_to')}
                    </Text>
                    <CommentQuote
                      authorName={replyTarget.authorName}
                      excerpt={commentExcerpt(replyTarget.content)}
                      onClick={() => scrollToComment(replyTarget.id)}
                    />
                    <Tooltip title={I18n.t('ticket_comments.cancel_reply')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        aria-label={I18n.t('ticket_comments.cancel_reply')}
                        onClick={handleCancelReply}
                      />
                    </Tooltip>
                  </div>
                )}

                <Form.Item
                  name="content"
                  className="ticket-comments-form__content-field"
                  getValueFromEvent={(value) => value}
                >
                  <CommentEditor
                    ticketId={ticketId}
                    placeholder="Add a comment..."
                    toolbarExtra={(
                      <CommentAttachmentButton
                        fileList={attachments}
                        setFileList={setAttachments}
                      />
                    )}
                  />
                </Form.Item>

                <CommentAttachments fileList={attachments} setFileList={setAttachments} />

                <div className="ticket-comments-form__footer">
                  {canSetInternalComment ? (
                    <Form.Item
                      name="internal"
                      className="ticket-comments-form__visibility"
                    >
                      <Radio.Group
                        optionType="button"
                        buttonStyle="solid"
                        size="small"
                        className="ticket-comments-form__visibility-toggle"
                        disabled={replyTarget?.internal ?? false}
                        options={[
                          {
                            value: false,
                            label: (
                              <span className="ticket-comments-form__visibility-option">
                                <GlobalOutlined />
                                {I18n.t('ticket_comments.visibility.public')}
                              </span>
                            ),
                          },
                          {
                            value: true,
                            label: (
                              <span className="ticket-comments-form__visibility-option">
                                <LockOutlined />
                                {I18n.t('ticket_comments.visibility.internal')}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </Form.Item>
                  ) : <div />}
                  <Tooltip title={submitButtonName}>
                    <Button
                      type="primary"
                      shape="circle"
                      size="small"
                      icon={<CheckOutlined />}
                      loading={isFormSending}
                      disabled={!content?.trim()}
                      onClick={() => form.submit()}
                      aria-label={submitButtonName}
                    />
                  </Tooltip>
                </div>
              </Form>
            </div>
          </div>
        </List.Item>
      ) : (
        <List.Item>
          <div className="ticket-comments-closed">
            <InfoCircleOutlined className="ticket-comments-closed__icon" />
            <Text className="ticket-comments-closed__text">
              {I18n.t('ticket_comments.closed_ticket')}
            </Text>
          </div>
        </List.Item>
      )}
    </>
  );
};

TicketComments.propTypes = {
  ticketId: number,
  currentUserInfo: shape({
    id: number,
    type: string,
    username: string,
    avatarUrl: string,
  }),
  canSetInternalComment: bool,
  canComment: bool,
  onDraftChange: func,
};

export default TicketComments;
